import { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ChevronLeft, Send, Paperclip, Image, FileText, Download, Hash } from 'lucide-react'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import { Button, Spinner } from '../../../components/ui'
import { useAuth } from '../../../contexts/AuthContext'
import usePusherChannel from '../../../hooks/usePusher'

interface Message {
  id: number
  user_id: number
  user_name: string
  user_avatar: string | null
  content: string | null
  media_url: string | null
  media_name: string | null
  media_type: string | null
  media_size: string | null
  created_at: string
}

interface Channel {
  id: number
  name: string
  slug: string
  color: string | null
  description: string | null
}

export default function CommunityChannelChatPage() {
  const { channelId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [channel, setChannel] = useState<Channel | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [file, setFile] = useState<File | null>(null)
  const bottomRef = useRef<HTMLDivElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    fetchMessages()
  }, [channelId])

  usePusherChannel(
    channelId ? `community.channel.${channelId}` : null,
    'new-message',
    (data: Message) => {
      setMessages(prev => prev.some(m => m.id === data.id) ? prev : [...prev, data])
    },
  )

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages.length])

  async function fetchMessages() {
    try {
      const res = await axios.get(buildApiUrl(`/v1/community/channels/${channelId}/messages`), { params: { per_page: 100 } })
      setMessages(res.data.data)
      if (res.data.channel) setChannel(res.data.channel)
    } catch (err: any) {
      if (loading) toast('error', 'Erro ao carregar mensagens')
    } finally { setLoading(false) }
  }

  async function handleSend(e: React.FormEvent) {
    e.preventDefault()
    if (!input.trim() && !file) return
    setSending(true)
    try {
      const fd = new FormData()
      if (input.trim()) fd.append('content', input)
      if (file) fd.append('file', file)
      await axios.post(buildApiUrl(`/v1/community/channels/${channelId}/messages`), fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      setInput('')
      setFile(null)
      if (fileRef.current) fileRef.current.value = ''
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Erro ao enviar')
    } finally { setSending(false) }
  }

  const formatTime = (d: string) => new Date(d).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })

  let lastUserId: number | null = null

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Spinner /></div>
  }

  return (
    <div className="flex flex-col h-[calc(100vh-120px)]">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b border-gray-200">
        <button onClick={() => navigate('/community/channels')} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
          <ChevronLeft className="w-5 h-5 text-gray-600" />
        </button>
        {channel && (
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center text-white text-xs font-bold"
              style={{ backgroundColor: channel.color || '#5865F2' }}
            >
              {channel.name.substring(0, 2).toUpperCase()}
            </div>
            <div>
              <div className="flex items-center gap-1.5">
                <Hash className="w-3.5 h-3.5 text-gray-400" />
                <h1 className="text-lg font-bold text-gray-900">{channel.name}</h1>
              </div>
              {channel.description && <p className="text-xs text-gray-500">{channel.description}</p>}
            </div>
          </div>
        )}
        <div className="ml-auto text-xs text-gray-400">{messages.length} mensagens</div>
      </div>

      {/* Mensagens */}
      <div className="flex-1 overflow-y-auto py-4 flex flex-col">
        <div className="mt-auto space-y-0.5">
          {messages.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-8">Nenhuma mensagem ainda. Envie a primeira!</p>
          )}
          {messages.map(msg => {
            const isNewGroup = msg.user_id !== lastUserId
            const isMe = msg.user_id === user?.id
            lastUserId = msg.user_id

            return (
              <div key={msg.id} className={`flex gap-2.5 px-1 py-0.5 rounded-lg hover:bg-gray-50 ${isNewGroup ? 'mt-4' : ''}`}>
                {/* Avatar */}
                <div className="w-8 shrink-0">
                  {isNewGroup && (
                    <div className="w-8 h-8 rounded-full bg-brand-100 text-brand-600 flex items-center justify-center text-xs font-bold">
                      {msg.user_name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  {isNewGroup && (
                    <div className="flex items-baseline gap-2 mb-0.5">
                      <span className={`text-sm font-semibold ${isMe ? 'text-brand-600' : 'text-gray-800'}`}>
                        {msg.user_name}
                      </span>
                      <span className="text-[11px] text-gray-400">{formatTime(msg.created_at)}</span>
                    </div>
                  )}

                  {msg.content && (
                    <p className="text-sm text-gray-700 leading-relaxed break-words">{msg.content}</p>
                  )}

                  {msg.media_url && (
                    <div className="mt-1">
                      {msg.media_type === 'image' ? (
                        <img src={msg.media_url} alt={msg.media_name || ''} className="max-w-xs max-h-48 rounded-lg border border-gray-200 object-cover" />
                      ) : (
                        <a href={msg.media_url} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors">
                          <FileText className="w-4 h-4 text-brand-500" />
                          <div>
                            <p className="text-sm font-medium text-gray-700">{msg.media_name}</p>
                            {msg.media_size && <p className="text-xs text-gray-400">{msg.media_size}</p>}
                          </div>
                          <Download className="w-4 h-4 text-gray-400" />
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )
          })}
          <div ref={bottomRef} />
        </div>
      </div>

      {/* Arquivo pendente */}
      {file && (
        <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-t-xl">
          {file.type.startsWith('image/') ? <Image className="w-4 h-4 text-brand-500" /> : <FileText className="w-4 h-4 text-brand-500" />}
          <span className="text-sm text-gray-700 flex-1 truncate">{file.name}</span>
          <button onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = '' }} className="text-xs text-gray-400 hover:text-error-500">Remover</button>
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSend} className="flex gap-2 pt-3 border-t border-gray-200">
        <button type="button" onClick={() => fileRef.current?.click()}
          className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors shrink-0">
          <Paperclip className="w-4 h-4" />
        </button>
        <input ref={fileRef} type="file" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) setFile(f) }} />

        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder={`Mensagem em #${channel?.name || ''}...`}
          className="flex-1 px-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:border-brand-300 focus:ring-2 focus:ring-brand-500/10 outline-none"
        />

        <Button type="submit" disabled={sending && !input.trim() && !file} loading={sending} className="shrink-0">
          <Send className="w-4 h-4" />
        </Button>
      </form>
    </div>
  )
}
